import { getAccessToken } from './getAccessToken';
import moment from 'moment';

// const environmentUrl = "https://org487f0635.crm8.dynamics.com"; // dev
const environmentUrl = "https://orgea61b289.crm8.dynamics.com";

// Data interface for Baking and Cooling save
export interface BakingAndCoolingData {
  cr3ea_qualitytourid: string;
  cr3ea_title: string;
  cr3ea_cycle: string;
  cr3ea_shift: string | null;
  cr3ea_tourstartdate: string;
  cr3ea_observedby: string | null;
  cr3ea_productname: string;
  cr3ea_platform: string;
  cr3ea_bakingtime: string | null;
  cr3ea_topbakingtempzone1: string | null;
  cr3ea_topbakingtempzone2: string | null;
  cr3ea_topbakingtempzone3: string | null;
  cr3ea_topbakingtempzone4: string | null;
  cr3ea_topproducttempafterbaking: string | null;
  cr3ea_pccf_ap: string | null;
  cr3ea_pccf_std: string | null;
  cr3ea_pccf_ad: string | null;
  cr3ea_ovenfirestarttime: string | null;
  cr3ea_ovenstoptime: string | null;
  cr3ea_productintime: string | null;
  cr3ea_productouttime: string | null;
  cr3ea_linenumber: string | null;
  cr3ea_recorddate: string | null;
  cr3ea_recordtime: string | null;
  cr3ea_qaexecutive: string | null;
  cr3ea_variety: string | null;
  cr3ea_coolingtime: string | null;
  cr3ea_coolingtemp: string | null;
  cr3ea_coretemp: string | null;
  cr3ea_productcolor: string | null;
  cr3ea_executivename: string | null;
}

export interface SaveResponse {
  success: boolean;
  message: string;
  data?: any;
}

/**
 * Collect estimation data for cycle save.
 * Multiple products per cycle: call once per product; each call persists one product for the given cycle.
 * @param cycleNum - Cycle number (1-based)
 * @param startFormData - Start session form data (productName, lineNumber, executiveName, bakingTime)
 * @param saveFormData - Save session form data (all the baking and cooling fields)
 * @param QualityTourId - Quality Tour ID from Redux
 * @param UserName - User name from MSAL accounts
 * @returns Promise with savedData array
 */
export async function collectEstimationDataCycleSave(
  cycleNum: number,
  startFormData: {
    productName: string;
    lineNumber: string;
    executiveName: string;
    bakingTime: string;
  },
  saveFormData: {
    bakingTempZone1: string;
    bakingTempZone2: string;
    bakingTempZone3: string;
    bakingTempZone4: string;
    productTempAfterBaking: string;
    ap: string;
    std: string;
    ad: string;
    ovenFireStartTime: string;
    ovenStopTime: string;
    productInTime: string;
    productOutTime: string;
    coolingTime: string;
    coolingTemp: string;
    coreTemp: string;
    productColor: string;
  },
  QualityTourId: string,
  UserName: string
): Promise<{ savedData: BakingAndCoolingData[] }> {
  const product = startFormData.productName || "N/A";
  const executiveName = startFormData.executiveName || "N/A";
  const bakingtime = startFormData.bakingTime || "N/A";
  const linenumber = startFormData.lineNumber || "";

  const bakingtempzone1 = saveFormData.bakingTempZone1 || "";
  const bakingtempzone2 = saveFormData.bakingTempZone2 || "";
  const bakingtempzone3 = saveFormData.bakingTempZone3 || "";
  const bakingtempzone4 = saveFormData.bakingTempZone4 || "";
  const producttempafterbaking = saveFormData.productTempAfterBaking || "";

  const pccf_ap = saveFormData.ap || "";
  const pccf_std = saveFormData.std || "";
  const pccf_ad = saveFormData.ad || "";

  const ovenfirestarttime = saveFormData.ovenFireStartTime || "";
  const ovenstoptime = saveFormData.ovenStopTime || "";
  const productintime = saveFormData.productInTime || "";
  const productouttime = saveFormData.productOutTime || "";

  const variety = ""; // Not in current form, but in API structure
  const coolingtime = saveFormData.coolingTime || "";
  const coolingtemp = saveFormData.coolingTemp || "";
  const coretemp = saveFormData.coreTemp || "";
  const productcolor = saveFormData.productColor || "";

  const recorddate = "";
  const recordtime = "";
  const qaexecutive = "";

  const data: BakingAndCoolingData = {
    "cr3ea_qualitytourid": QualityTourId,
    "cr3ea_title": 'Baking_' + moment().format('MM-DD-YYYY'),
    "cr3ea_cycle": `Cycle-${cycleNum}`,
    "cr3ea_shift": sessionStorage.getItem("shiftValue") || "shift 1",
    "cr3ea_tourstartdate": moment().format('MM-DD-YYYY'),
    "cr3ea_observedby": UserName || null,
    "cr3ea_productname": product,
    "cr3ea_platform": "Web",
    "cr3ea_bakingtime": bakingtime,

    "cr3ea_topbakingtempzone1": bakingtempzone1 || null,
    "cr3ea_topbakingtempzone2": bakingtempzone2 || null,
    "cr3ea_topbakingtempzone3": bakingtempzone3 || null,
    "cr3ea_topbakingtempzone4": bakingtempzone4 || null,
    "cr3ea_topproducttempafterbaking": producttempafterbaking || null,

    "cr3ea_pccf_ap": pccf_ap || null,
    "cr3ea_pccf_std": pccf_std || null,
    "cr3ea_pccf_ad": pccf_ad || null,

    "cr3ea_ovenfirestarttime": ovenfirestarttime || null,
    "cr3ea_ovenstoptime": ovenstoptime || null,
    "cr3ea_productintime": productintime || null,
    "cr3ea_productouttime": productouttime || null,

    "cr3ea_linenumber": linenumber || null,

    "cr3ea_recorddate": recorddate || null,
    "cr3ea_recordtime": recordtime || null,
    "cr3ea_qaexecutive": qaexecutive || null,

    "cr3ea_variety": variety || null,
    "cr3ea_coolingtime": coolingtime || null,
    "cr3ea_coolingtemp": coolingtemp || null,
    "cr3ea_coretemp": coretemp || null,
    "cr3ea_productcolor": productcolor || null,

    "cr3ea_executivename": executiveName || null
  };

  return { savedData: [data] };
}

/**
 * Save section API call
 * Saves records to cr3ea_prod_bakingandcoolings table
 * @param optionalAccessToken - If provided (e.g. during sync), use this token instead of calling getAccessToken
 */
export async function savesectionApicall(data: BakingAndCoolingData[], optionalAccessToken?: string): Promise<SaveResponse> {
  try {
    const buttons = document.querySelectorAll("button");
    buttons.forEach(button => (button as HTMLButtonElement).disabled = true);

    let accessToken: string | undefined = optionalAccessToken;
    if (!accessToken) {
      const tokenResult = await getAccessToken();
      accessToken = tokenResult?.token;
    }
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "Content-Type": "application/json; charset=utf-8",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Prefer": "return=representation",
      "Authorization": `Bearer ${accessToken}`
    };

    const apiVersion = "9.2";
    const tableName = "cr3ea_prod_bakingandcoolings";
    const apiUrl = `${environmentUrl}/api/data/v${apiVersion}/${tableName}`;

    const results: any[] = [];
    for (const record of data) {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify(record)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to save record: ${response.status} - ${errorText}`);
      }
      
      const result = await response.json();
      console.log('Record saved:', result);
      results.push(result);
    }

    buttons.forEach(button => (button as HTMLButtonElement).disabled = false);
    return { 
      success: true, 
      message: `${data.length} records saved successfully`, 
      data: results 
    };
  } catch (error) {
    console.error('Error saving records:', error);
    const buttons = document.querySelectorAll("button");
    buttons.forEach(button => (button as HTMLButtonElement).disabled = false);
    return { 
      success: false, 
      message: error instanceof Error ? error.message : 'Failed to save records. Please try again.', 
      data: [] 
    };
  }
}

// Interface for cycle data
export interface CycleData {
  cycleNum: string;
  product: string;
  executiveName: string;
  bakingTime: string;
  lineNumber: string;
  bakingtempzone1: string;
  bakingtempzone2: string;
  bakingtempzone3: string;
  bakingtempzone4: string;
  producttempafterbaking: string;
  pccf_ap: string;
  pccf_std: string;
  pccf_ad: string;
  ovenfirestarttime: string;
  ovenstoptime: string;
  productintime: string;
  productouttime: string;
  variety: string;
  coolingtime: string;
  coolingtemp: string;
  coretemp: string;
  productcolor: string;
}

/**
 * Fetch completed cycles for Baking and Cooling
 * @param QualityTourId - Quality Tour ID from Redux
 * @param optionalAccessToken - If provided (e.g. during offline setup), use this token instead of calling getAccessToken
 * @returns Promise<CycleData[]>
 */
export async function fetchCycleData(QualityTourId: string, optionalAccessToken?: string | null): Promise<CycleData[]> {
  try {
    let accessToken: string | undefined = optionalAccessToken ?? undefined;
    if (!accessToken) {
      const tokenResult = await getAccessToken();
      accessToken = tokenResult?.token;
    }
    if (!accessToken) {
      throw new Error("Access token is invalid or missing");
    }

    const headers = {
      "Accept": "application/json",
      "OData-MaxVersion": "4.0",
      "OData-Version": "4.0",
      "Authorization": `Bearer ${accessToken}`
    };

    const apiVersion = "9.2";
    const tableName = "cr3ea_prod_bakingandcoolings";

    const qualityTourGuid = (QualityTourId || "").replace(/[{}]/g, "");

    const selectFields = [
      'cr3ea_cycle',
      'cr3ea_productname',
      'cr3ea_bakingtime',
      'cr3ea_executivename',
      'cr3ea_topbakingtempzone1',
      'cr3ea_topbakingtempzone2',
      'cr3ea_topbakingtempzone3',
      'cr3ea_topbakingtempzone4',
      'cr3ea_topproducttempafterbaking',
      'cr3ea_pccf_ap',
      'cr3ea_pccf_std',
      'cr3ea_pccf_ad',
      'cr3ea_ovenfirestarttime',
      'cr3ea_ovenstoptime',
      'cr3ea_productintime',
      'cr3ea_productouttime',
      'cr3ea_linenumber',
      'cr3ea_variety',
      'cr3ea_coolingtime',
      'cr3ea_coolingtemp',
      'cr3ea_coretemp',
      'cr3ea_productcolor'
    ].join(',');

    const buildUrl = (filter: string) =>
      `${environmentUrl}/api/data/v${apiVersion}/${tableName}` +
      `?$filter=${encodeURIComponent(filter)}` +
      `&$select=${selectFields}`;

    // Try 1: direct column equals 'guid-string'
    let filter = `cr3ea_qualitytourid eq '${qualityTourGuid}'`;
    let response = await fetch(buildUrl(filter), { headers });

    // Retry assuming lookup column
    if (!response.ok) {
      const errText = await response.text();
      if (errText.includes("Unrecognized") || errText.includes("Edm.") || errText.includes("$filter")) {
        filter = `_cr3ea_qualitytourid_value eq '${qualityTourGuid}'`;
        response = await fetch(buildUrl(filter), { headers });

        if (!response.ok) {
          throw new Error(`Failed to fetch data (retry): ${response.status} - ${await response.text()}`);
        }
      } else {
        throw new Error(`Failed to fetch data: ${response.status} - ${errText}`);
      }
    }

    const data = await response.json();
    const cycles: CycleData[] = [];

    data.value.forEach((record: any) => {
      const cycleNum = (record.cr3ea_cycle || '').replace('Cycle-', '');
      if (!cycleNum) return;

      // Create a new CycleData object for each record (multiple products per cycle)
      const cycleData: CycleData = {
        cycleNum,
        product: record?.cr3ea_productname || "N/A",
        executiveName: record?.cr3ea_executivename || "N/A",
        bakingTime: record?.cr3ea_bakingtime || "N/A",
        lineNumber: record?.cr3ea_linenumber ?? "",

        bakingtempzone1: record?.cr3ea_topbakingtempzone1 ?? "",
        bakingtempzone2: record?.cr3ea_topbakingtempzone2 ?? "",
        bakingtempzone3: record?.cr3ea_topbakingtempzone3 ?? "",
        bakingtempzone4: record?.cr3ea_topbakingtempzone4 ?? "",
        producttempafterbaking: record?.cr3ea_topproducttempafterbaking ?? "",

        pccf_ap: record?.cr3ea_pccf_ap ?? "",
        pccf_std: record?.cr3ea_pccf_std ?? "",
        pccf_ad: record?.cr3ea_pccf_ad ?? "",

        ovenfirestarttime: record?.cr3ea_ovenfirestarttime ?? "",
        ovenstoptime: record?.cr3ea_ovenstoptime ?? "",
        productintime: record?.cr3ea_productintime ?? "",
        productouttime: record?.cr3ea_productouttime ?? "",

        variety: record?.cr3ea_variety ?? "",
        coolingtime: record?.cr3ea_coolingtime ?? "",
        coolingtemp: record?.cr3ea_coolingtemp ?? "",
        coretemp: record?.cr3ea_coretemp ?? "",
        productcolor: record?.cr3ea_productcolor ?? ""
      };

      // Try to enhance with localStorage data if available (for backward compatibility)
      const storedData = localStorage.getItem(`cycle-${cycleNum}-start-data`);
      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);
          if (parsed.product) cycleData.product = parsed.product;
          if (parsed.executiveName) cycleData.executiveName = parsed.executiveName;
          if (parsed.bakingTime) cycleData.bakingTime = parsed.bakingTime;
          if (parsed.lineNumber) cycleData.lineNumber = parsed.lineNumber;
        } catch (_) {
          // Ignore parse errors
        }
      }

      cycles.push(cycleData);
    });

    return cycles;
  } catch (error) {
    console.error('Error fetching cycle data:', error);
    return [];
  }
}
